// api/KanbanTaskApiClient.ts
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import SkTechApiClient from "./base/SkTechApiClient";
import type { ConfigApi } from "./base/ConfigApi";
import type { ExportTimeLogsParams, KanbanTask, KanbanTaskComment, NewKanbanTask, NewKanbanTaskComment, KanbanTaskChange, KanbanTaskStatus, PublicProject } from '../stores/KanbanTaskStores';
import { AUTHENTICATED_ROUTES, kanbanPaths } from './kanban/kanbanRoutes';
import type { KanbanMode, KanbanPaths, KanbanRoutes } from './kanban/kanbanRoutes';
import type { Attachment, KanbanTaskAttachmentDto } from './kanban/attachments';
import { toAttachment } from './kanban/attachments';
import { toStatusNotEmptyError } from '../stores/kanban/statusErrors';

// Re-exported so the existing `import type { Attachment } from '.../KanbanTaskApiClient'` call sites
// keep working. It sits below the imports because `export ... from` counts as a module-body
// statement, and anything imported after one trips eslint's `import/first` in the CRA build.
export type { Attachment } from './kanban/attachments';

// `getTaskTimeLogs` uses `dayjs.utc`; the plugin used to be registered by AnonymousApiClient.
dayjs.extend(utc);
dayjs.extend(localizedFormat);

export type TaskTimeLog = {
  id: string;
  taskId: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    photoId?: string;
  };
  startTime?: Dayjs;
  endTime: Dayjs;
  totalTime: string; // in format 'hh:mm:ss'
  description: string;
};

export type TaskTimeMetrics = {
  taskId: string;
  estimatedTime: string;
  timeSpent: string;
  remainingTime: string;
  isOvertime: boolean;
  overtimeAmount: string;
  percentComplete: number;
};

export type ScheduledAndPriorityUpdate = {
  startDate?: string;
  dueDate?: string;
  priority: number;
}

export default class KanbanTaskApiClient extends SkTechApiClient {
  private routes: KanbanRoutes;
  private paths: KanbanPaths;

  constructor(config: ConfigApi, routes: KanbanRoutes = AUTHENTICATED_ROUTES) {
    super(config);
    this.routes = routes;
    this.paths = kanbanPaths(routes);
  }

  /** Switches every path this client builds; `KanbanTaskStore.setMode` reaches it through `ApiStore.setKanbanRoutes`. */
  setRoutes(routes: KanbanRoutes): void {
    this.routes = routes;
    this.paths = kanbanPaths(routes);
  }

  get mode(): KanbanMode {
    return this.routes.mode;
  }

  //#region General methods for task
  public async getTasks(projectId: string): Promise<KanbanTask[]> {
    return this.Get(this.paths.tasksByProject(projectId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((task: any) => ({
            ...task,
            // Parse createdDate, updatedDate
            createdDate: dayjs(task.createdDate),
            updatedDate: dayjs(task.updatedDate),
            startDate: task.startDate ? dayjs(task.startDate) : undefined,
            dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public async getTaskById(taskId: string): Promise<KanbanTask | null> {
    return this.Get(this.paths.task(taskId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async getDeletedTasks(projectId: string): Promise<KanbanTask[]> {
    return this.Get(this.paths.deletedTasksByProject(projectId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((task: any) => ({
            ...task,
            createdDate: dayjs(task.createdDate),
            updatedDate: dayjs(task.updatedDate),
            startDate: task.startDate ? dayjs(task.startDate) : undefined,
            dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public async getTaskDetails(taskIdOrPublicId: string, projectId?: string): Promise<KanbanTask | null> {
    return this.Get(this.paths.task(taskIdOrPublicId, projectId))
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(`Error fetching task: ${taskIdOrPublicId}`, err);
        return null;
      });
  }

  public async createTask(task: NewKanbanTask): Promise<KanbanTask | null> {
    return this.Post(this.paths.createTask(), task)
      .then((response) => {
        // server returns 201 on success
        if (response?.status === 200 || response?.status === 201) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTask(taskId: string, task: KanbanTask): Promise<KanbanTask | null> {
    task.createdByUser = undefined;
    return this.Put(this.paths.task(taskId), task)
      .then((response) => {
        // We now expect a 200 with the updated task in response.data
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async restoreTask(taskIdOrPublicId: string): Promise<KanbanTask | null> {
    return this.Patch(this.paths.restoreTask(taskIdOrPublicId), {})
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error("Error restoring task:", err);
        return null;
      });
  }

  async bulkRestore(taskIds: string[]) {
    return this.Patch(this.paths.restoreBulk(), {
      taskIds
    });
  }

  public async deleteTask(taskId: string): Promise<boolean> {
    return this.Delete(this.paths.task(taskId))
      .then((response) => response?.status === 200 || response?.status === 204)
      .catch((err) => {
        console.error(err);
        return false;
      });
  }

  async bulkSoftDelete(taskIds: string[]) {
    return this.Delete(this.paths.deleteBulk(), {
      taskIds,
      permanentDelete: false
    });
  }

  public async deleteTaskPermanent(taskId: string): Promise<boolean> {
    return this.Delete(this.paths.permanentDelete(taskId))
      .then((response) => response?.status === 200 || response?.status === 204)
      .catch((err) => {
        console.error(err);
        return false;
      });
  }

  async bulkPermanentDelete(taskIds: string[]) {
    return this.Delete(this.paths.permanentDeleteBulk(), {
      taskIds,
      permanentDelete: true
    });
  }
  //#endregion



  //#region Task Time Methods
  public async getTaskTimeMetrics(taskId: string): Promise<TaskTimeMetrics | null> {
    return this.Get(this.paths.timeMetrics(taskId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error fetching task time metrics:', err);
        return null;
      });
  }

  public async updateEstimatedTime(taskId: string, estimatedTimeStr: string): Promise<TaskTimeMetrics | null> {
    return this.Post(this.paths.estimatedTime(taskId), { estimatedTimeStr })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error updating estimated time:', err);
        return null;
      });
  }

  public async logTimeForTask(taskId: string, time: string, description: string, userId?: string, endDate?: string): Promise<any> {
    return this.Post(this.paths.logTime(taskId), {
      time,
      description,
      userId,
      endDate: endDate ? endDate : undefined
    })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error logging time for task:', err);
        return null;
      });
  }

  public async getTaskTimeLogs(taskId: string): Promise<TaskTimeLog[]> {
    return this.Get(this.paths.timeLogs(taskId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((log: any) => ({
            ...log,
            startTime: log.startTime ? dayjs.utc(log.startTime).local() : undefined,
            endTime: dayjs.utc(log.endTime).local()
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching task time logs:', err);
        return [];
      });
  }
  //#endregion



  //#region Move Task API Methods
  async moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void> {
    await this.Post(this.paths.move(taskId), {
      toStatusId: Number(toStatusId),
      toIndex
    });
  }

  public async updateTaskOrder(taskId: string, newIndex: number): Promise<KanbanTask | null> {
    return this.Patch(this.paths.order(taskId), { newIndex })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Task Change History
  public async getTaskChanges(taskId: string): Promise<KanbanTaskChange[]> {
    return this.Get(this.paths.changes(taskId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((item: any) => ({
            ...item,
            createdDate: dayjs(item.createdDate),
          })) as KanbanTaskChange[];
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }
  //#endregion



  //#region Custom Field API Methods
  public async getProjectCustomFields(projectId: string): Promise<any[]> {
    return this.Get(this.paths.customFieldsByProject(projectId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching project custom fields:', err);
        return [];
      });
  }

  public async getTaskCustomFieldValues(taskId: string): Promise<any[]> {
    return this.Get(this.paths.customFieldValuesByTask(taskId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching task custom field values:', err);
        return [];
      });
  }

  public async setTaskCustomFieldValue(taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean> {
    return this.Put(this.paths.customFieldValue(taskId, fieldId), { value, userId })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error setting custom field value:', err);
        return false;
      });
  }

  public async updateTaskCustomFieldValues(taskId: string, fieldValues: Record<string, string>): Promise<boolean> {
    return this.Put(this.paths.customFieldValuesBatch(taskId), { fieldValues })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error updating task custom field values:', err);
        return false;
      });
  }

  public async createCustomField(field: any): Promise<any> {
    return this.Post(this.paths.createCustomField(), field)
      .then((response) => {
        if (response?.status === 201) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error creating custom field:', err);
        return null;
      });
  }

  public async updateCustomField(fieldId: string, field: any): Promise<any> {
    return this.Put(this.paths.customField(fieldId), field)
      .then((response) => {
        if (response?.status === 204) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error updating custom field:', err);
        return null;
      });
  }

  public async deleteCustomField(fieldId: string): Promise<boolean> {
    return this.Delete(this.paths.customField(fieldId))
      .then((response) => {
        return response?.status === 204;
      })
      .catch((err) => {
        console.error('Error deleting custom field:', err);
        return false;
      });
  }

  public async updateCustomFieldOrder(fieldId: string, newOrder: number): Promise<boolean> {
    return this.Patch(this.paths.customFieldOrder(fieldId), { newOrder })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error updating custom field order:', err);
        return false;
      });
  }
  //#endregion



  //#region Attachment API Methods
  /**
   * `projectId` should be passed by the caller (the store already has it on the task it holds);
   * falling back to a `getTaskDetails` round trip only when the caller doesn't have it cached.
   * Deliberately does not catch: a 409 (`FileAlreadyExists`) must reach the drawer, which asks
   * whether to replace the file and retries with `confirmation`.
   */
  public async uploadTaskAttachment(taskId: string, file: File, confirmation: boolean = false, projectId?: string): Promise<Attachment | null> {
    let resolvedProjectId = projectId;
    if (!resolvedProjectId) {
      const task = await this.getTaskDetails(taskId);
      if (!task) {
        console.error(`Could not find task with ID ${taskId}`);
        return null;
      }
      resolvedProjectId = task.projectId;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('taskId', taskId);
    formData.append('projectId', String(resolvedProjectId));

    const url = this.paths.uploadAttachment(confirmation);
    // 409 FileAlreadyExists / 400 InvalidFile / 502 UploadFailed reject with the AxiosError; the drawer reads response.status/data.
    const response = await this.Post(url, formData);
    if (response?.status === 200 && response.data?.publicId) {
      return toAttachment(response.data as KanbanTaskAttachmentDto);
    }
    console.error('Upload failed, response status:', response?.status);
    return null;
  }

  public async getTaskAttachments(taskId: string): Promise<Attachment[]> {
    return this.Get(this.paths.attachments(taskId))
      .then((response) => {
        if (response?.status === 200 && Array.isArray(response.data)) {
          return (response.data as KanbanTaskAttachmentDto[]).map(toAttachment);
        }
        console.warn('Unexpected attachments response status:', response?.status);
        return [];
      })
      .catch((err) => {
        console.error('Error in getTaskAttachments:', err);
        return [];
      });
  }

  /** Uploads an image referenced by the task's rich-text description/comments. Returns the hosted URL. */
  public async uploadTaskImage(taskId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.Post(this.paths.images(taskId), formData);
    return response.data.url;
  }

  /** `DELETE {tasks}/{taskId}/attachments/{publicId}` (204); the public id keeps its slashes. */
  public async deleteAttachment(taskId: string, publicId: string): Promise<boolean> {
    return this.Delete(this.paths.attachment(taskId, publicId))
      .then((response) => response?.status === 200 || response?.status === 204)
      .catch((err) => {
        console.error('Error in deleteAttachment:', err);
        return false;
      });
  }
  //#endregion



  //#region Task Status Update
  public async updateTaskStatus(taskId: string, statusId: string): Promise<KanbanTask | null> {
    return this.Patch(this.paths.status(taskId), { statusId: statusId })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Task Comments API Methods
  public getTaskComments = async (taskId: string): Promise<KanbanTaskComment[]> => {
    return this.Get(this.paths.comments(taskId))
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((comment: any) => ({
            ...comment,
            // Use createdDate for comments
            createdDate: dayjs(comment.createdDate),
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  };

  public async createComment(comment: NewKanbanTaskComment): Promise<KanbanTaskComment | null> {
    return this.Post(this.paths.comments(comment.taskId), comment)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public deleteComment = async (commentId: string): Promise<boolean> => {
    return await this.Delete(this.paths.comment(commentId))
      .then(response => response?.status === 200);
  }
  //#endregion



  //#region Individual Field Updates
  public async updateTaskTitle(taskId: string, title: string): Promise<KanbanTask | null> {
    return this.Patch(this.paths.title(taskId), { title: title })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  /**
   * Deliberately does not catch: the store needs the rejected promise (and its
   * `error.response.data.code`) to roll back its optimistic update and detect
   * `InlineImagesNotAllowed` (400) saves.
   */
  public async updateTaskDescription(taskId: string, description: string): Promise<KanbanTask | null> {
    return this.Patch(this.paths.description(taskId), { description: description })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      });
  }

  public async updateTaskScheduledAndPriority(taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<KanbanTask | null> {
    return this.Patch(this.paths.scheduledAndPriority(taskId), scheduledAndPriority)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskAssignee(taskId: string, assigneeId?: string): Promise<KanbanTask | null> {
    return this.Patch(this.paths.assignee(taskId, assigneeId))
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }
  //#endregion



  //#region Task Status API Methods
  /**
   * Deliberately does not catch: the public board validates its link with this call and needs the
   * rejected promise (401) to send the visitor to /link-expired.
   */
  public async getProjectStatuses(projectId: string): Promise<KanbanTaskStatus[]> {
    const response = await this.Get(this.paths.statusesByProject(projectId));
    return response?.status === 200 && Array.isArray(response.data) ? response.data : [];
  }

  public async createStatus(name: string, projectId: string, index: number, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> {
    const status = {
      name,
      projectId: Number(projectId),
      index,
      isFinalStatus
    };

    return this.Post(this.paths.createStatus(), status)
      .then((response) => {
        if (response?.status === 201) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateStatus(statusId: string, status: KanbanTaskStatus): Promise<KanbanTaskStatus | null> {
    return this.Put(this.paths.statusById(statusId), status)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  /** Resolves true on 204; throws StatusNotEmptyError on 409 StatusNotEmpty; false on any other failure. */
  public async deleteStatus(statusId: string, moveTasksTo?: number): Promise<boolean> {
    try {
      const response = await this.Delete(this.paths.deleteStatus(statusId, moveTasksTo));
      return response?.status === 204;
    } catch (err) {
      const notEmpty = toStatusNotEmptyError(err);
      if (notEmpty) throw notEmpty;
      console.error(err);
      return false;
    }
  }

  public async updateStatusOrder(statusId: string, newIndex: number): Promise<KanbanTaskStatus | null> {
    return this.Patch(this.paths.statusOrder(statusId), { newIndex })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  /** `GET {tasks}/project` — the `PublicProjectDto` of the board token's project (public mode only; server Task 7). */
  public async getProject(): Promise<PublicProject> {
    const response = await this.Get(this.paths.project());
    const data = response?.data ?? {};
    return {
      id: Number(data.id),
      name: String(data.name ?? ''),
      slug: data.slug ?? undefined,
      companyId: data.companyId ?? undefined,
    };
  }
  //#endregion



  //#region Export Time Logs API Methods
  public async exportProjectTimeLogs(params: ExportTimeLogsParams):
    Promise<{ blob: Blob; headers: any } | null> {
    return this.PostBlob(this.paths.exportTimeLogs(), params)
      .then((response: any) => {
        if (response?.status >= 200 && response?.status < 300) {
          return { blob: response.data as Blob, headers: response.headers };
        }
        return null;
      })
      .catch((err) => {
        console.error('exportProjectTimeLogs error:', err);
        return null;
      });
  }
  //#endregion
}